import { IconIcons, IconLoader2, IconPhotoUp, IconTrash } from "@tabler/icons-react"
import { type ChangeEvent, type FC, useCallback, useEffect, useState } from "react"

import type { LandingPageFeaturesBlock } from "@forento/core/models/landing"
import { isLandingFeatureIcon } from "@forento/core/models/landingFeatureIcon"
import { landingPageCopyCharacterLimits } from "@forento/core/schemas/landing"
import LandingFeatureIcon from "@forento/ui/components/LandingFeatureIcon"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import useFileUpload from "~/hooks/useFileUpload"

import EditableField from "./EditableField"
import EditorShell from "./EditorShell"
import FeatureIconPickerModal from "./FeatureIconPickerModal"
import RichEditableField from "./RichEditableField"

type Props = {
	block: LandingPageFeaturesBlock
	onChange: (block: LandingPageFeaturesBlock) => void
	onClose: () => void
}
const FeaturesBlockEditor: FC<Props> = ({ block, onChange, onClose }) => {
	const [title, setTitle] = useState(block.content.title ?? "")
	const [features, setFeatures] = useState(block.content.features)
	const [iconPickerFeatureIndex, setIconPickerFeatureIndex] = useState<number>()
	const [uploadingFeatureIndexes, setUploadingFeatureIndexes] = useState<Set<number>>(new Set())

	const imageUpload = useFileUpload()

	useEffect(() => {
		onChange({
			type: "features",
			content: { title: title.trim() || undefined, features: features.filter(f => f.title.trim()) },
		})
	}, [features, onChange, title])

	const handleFeatureTitleChange = (index: number, value: string) => {
		const newFeatures = [...features]
		newFeatures[index] = { ...newFeatures[index], title: value }
		setFeatures(newFeatures)
	}

	const handleFeatureDescriptionChange = useCallback(
		(index: number) => (value: string | null) => {
			setFeatures(prev => {
				const newFeatures = [...prev]
				newFeatures[index] = { ...newFeatures[index], description: value ?? undefined }
				return newFeatures
			})
		},
		[],
	)

	const handleFeatureIconUpload = async (index: number, file: File) => {
		setUploadingFeatureIndexes(prev => new Set(prev).add(index))
		try {
			const uploaded = await imageUpload.upload({ file, kind: "landing-image" })
			if (uploaded !== null) {
				setFeatures(prev =>
					prev.map((feature, currentIndex) =>
						currentIndex === index ? { ...feature, icon: uploaded.publicUrl } : feature,
					),
				)
			}
		} catch (error) {
			console.error(error)
			setUploadingFeatureIndexes(prev => {
				const next = new Set(prev)
				next.delete(index)
				return next
			})
			return Promise.reject(error)
		}
		setUploadingFeatureIndexes(prev => {
			const next = new Set(prev)
			next.delete(index)
			return next
		})
	}

	const handleFeatureImageChange = (index: number) => async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		event.target.value = ""
		if (file !== undefined) await handleFeatureIconUpload(index, file)
	}

	const setFeatureIcon = (index: number, icon: string | undefined) => {
		setFeatures(prev =>
			prev.map((feature, currentIndex) => (currentIndex === index ? { ...feature, icon } : feature)),
		)
	}

	return (
		<EditorShell onClose={onClose}>
			<section className="px-6 pt-16 pb-8 sm:pt-20 lg:px-8">
				<div className="mx-auto max-w-7xl">
					<EditableField
						value={title}
						onChange={setTitle}
						placeholder="Section title"
						className="mb-12 text-center"
						maxCharacters={landingPageCopyCharacterLimits.title}
						inputClassName="text-center font-bold text-3xl text-gray-900 tracking-tight sm:text-4xl"
					/>
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
						{features.map((feature, index) => (
							<div
								key={index}
								className={cn(
									"relative flex flex-col rounded-2xl bg-white p-8 shadow-lg ring-1 shadow-gray-900/5 ring-gray-200/50",
								)}
							>
								<div className="mb-6">
									<div className="mb-3 flex items-center justify-between gap-3">
										<div className="text-xs font-medium tracking-[0.18em] text-gray-500 uppercase">
											Icon
										</div>
										<button
											type="button"
											onClick={() => setFeatures(prev => prev.filter((_, i) => i !== index))}
											className="cursor-pointer text-xs text-gray-400 transition-colors hover:text-red-600"
										>
											Remove
										</button>
									</div>
									<div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
										{feature.icon && (
											<div className="text-primary mb-4 flex size-14 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-gray-200/70">
												{isLandingFeatureIcon(feature.icon) ? (
													<LandingFeatureIcon value={feature.icon} className="size-7" />
												) : (
													<img
														src={feature.icon}
														alt={feature.title}
														className="size-7 object-contain"
													/>
												)}
											</div>
										)}
										<div className="flex flex-wrap gap-2">
											<button
												type="button"
												onClick={() => setIconPickerFeatureIndex(index)}
												className="border-primary text-primary hover:bg-primary/5 flex cursor-pointer items-center gap-2 rounded-lg border bg-white px-3 py-2 text-xs font-semibold transition-colors"
											>
												<IconIcons className="size-4" />
												{isLandingFeatureIcon(feature.icon ?? "")
													? "Change icon"
													: "Choose icon"}
											</button>
											<label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900">
												<input
													type="file"
													accept=".png,.jpg,.jpeg,.gif,.svg,.webp"
													disabled={uploadingFeatureIndexes.has(index)}
													onChange={handleFeatureImageChange(index)}
													className="sr-only"
												/>
												{uploadingFeatureIndexes.has(index) ? (
													<IconLoader2 className="size-4 animate-spin" />
												) : (
													<IconPhotoUp className="size-4" />
												)}
												Upload image
											</label>
											{feature.icon && (
												<button
													type="button"
													onClick={() => setFeatureIcon(index, undefined)}
													aria-label="Remove feature icon"
													className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-500 transition-colors hover:border-red-200 hover:text-red-600"
												>
													<IconTrash className="size-4" />
													Remove
												</button>
											)}
										</div>
									</div>
								</div>
								<EditableField
									value={feature.title}
									onChange={value => handleFeatureTitleChange(index, value)}
									placeholder="Feature title"
									maxCharacters={landingPageCopyCharacterLimits.title}
									inputClassName="font-bold text-gray-900 text-xl tracking-tight"
								/>
								<RichEditableField
									initialValue={feature.description ?? ""}
									onChange={handleFeatureDescriptionChange(index)}
									className="mt-3"
									maxCharacters={landingPageCopyCharacterLimits.itemDescription}
								/>
							</div>
						))}
					</div>
					<Button
						variant="secondary"
						onClick={() => setFeatures(prev => [...prev, { title: "", description: "" }])}
						className="mt-6 cursor-pointer"
					>
						Add feature
					</Button>
				</div>
			</section>
			<FeatureIconPickerModal
				isOpen={iconPickerFeatureIndex !== undefined}
				selectedValue={
					iconPickerFeatureIndex === undefined ? undefined : features[iconPickerFeatureIndex]?.icon
				}
				onSelect={value => {
					if (iconPickerFeatureIndex !== undefined) setFeatureIcon(iconPickerFeatureIndex, value)
				}}
				onClose={() => setIconPickerFeatureIndex(undefined)}
			/>
		</EditorShell>
	)
}

export default FeaturesBlockEditor
